/**
 * Contrato zod del detalle de un comprobante traído desde ACSIS.
 *
 * El backend (everex-traza-back · GET /api/despacho/comprobante/:referencia)
 * normaliza el response de Plataforma a este shape:
 *   - `comprobante`: header (división, sucursal, número, fechaEmisión, cliente)
 *   - `items[]`: renglones del remito (sku · cantidad · descripción)
 *   - `partidas[]`: líneas trazables (sku · lote · N°serie · vencimiento)
 *
 * Las series y los lotes viven en `partidas[]`, NO en `items[]`.
 * Ver docs/integraciones/acsis-plataforma.md
 */

import { z } from 'zod';
import { TareaTipoSchema } from './tareas';

export const ComprobanteItemSchema = z.object({
  renglon: z.string().nullable(),
  sku: z.string().nullable(),
  /** GTIN del producto — `codigoDeBarras` del maestro de artículo de ACSIS,
   *  resuelto en el backend por SKU. Null si el artículo no existe o no
   *  tiene el código cargado en el ERP. */
  gtin: z.string().nullable().optional(),
  /** Nombre del artículo (maestro ACSIS `nombre`). Null si no se resolvió. */
  nombre: z.string().nullable().optional(),
  /** Trazable ANMAT (clasificador5 = 'SI'). Null si no se resolvió. */
  trazable: z.boolean().nullable().optional(),
  descripcion: z.string().nullable(),
  /** Unidades del renglón. En remitos por lote, la suma de `cantidad` de sus partidas. */
  cantidad: z.number().nullable(),
  /** Lo que informa el renglón en ACSIS (`cantidadIngresada`). */
  cantidadIngresada: z.number().nullable().optional(),
  unidadDeMedida: z.string().nullable(),
  /** ID del depósito de origen (crudo de ACSIS). */
  deposito: z.string().nullable(),
  /** Nombre del depósito de origen (resuelto en el sync contra el catálogo
   *  de depósitos de ACSIS). Null si no se resolvió — el front cae al `deposito`. */
  depositoNombre: z.string().nullable().optional(),
  signo: z.string().nullable(),
});

export const ComprobantePartidaSchema = z.object({
  /** ID local de `erp_task_detail` — se muestra en el detalle. Opcional
   *  para tolerar fallback al JSON crudo si la tabla detalle está vacía. */
  id: z.number().nullable().optional(),
  /** ID local de `stock` (LEFT JOIN por erp_task_detail_id). Null si no
   *  hay fila stock asociada (ej. el sync no resolvió GTIN). */
  stockId: z.number().nullable().optional(),
  /** FK a product_var de la fila de stock. Null si el stock no está vinculado
   *  al maestro (permite el backfill manual desde el detalle). */
  productVarId: z.number().nullable().optional(),
  sku: z.string().nullable(),
  /** Nombre del artículo (maestro ACSIS). Null si no se resolvió. */
  nombre: z.string().nullable().optional(),
  /** GTIN del artículo (product_var). Null si no está resuelto. */
  gtin: z.string().nullable().optional(),
  /** Trazable ANMAT (clasificador5 = 'SI'). Null si no se resolvió. */
  trazable: z.boolean().nullable().optional(),
  /** Nº de lote (en ACSIS vive como `partidaOrigen`). */
  lote: z.string().nullable(),
  partidaEmpresa: z.string().nullable(),
  numeroDeSerie: z.string().nullable(),
  fechaVencimiento: z.string().nullable(),
  fechaAlta: z.string().nullable(),
  /** Cantidad de alta del lote en ACSIS (ej. 100). NO es la del remito. */
  cantidadInicial: z.number().nullable(),
  /** Unidades de esta partida en el remito (formato por lote). Null si no vino. */
  cantidad: z.number().nullable().optional(),
  /** Nombre del depósito de origen (persistido en el sync). */
  depositoNombre: z.string().nullable().optional(),
});

export const ComprobanteHeaderSchema = z.object({
  division: z.string().nullable(),
  sucursalImpositiva: z.string().nullable(),
  tipo: z.string().nullable(),
  numero: z.string().nullable(),
  fechaEmision: z.string().nullable(),
  clienteId: z.string().nullable(),
  renglonDomicilio: z.string().nullable(),
  transporte: z.string().nullable(),
  cantidadBultos: z.number().nullable(),
  pesoEmbalado: z.number().nullable(),
  origen: z.string().nullable(),
  esFacturable: z.string().nullable(),
});

/** Estado del cierre de tarea en el ERP (POST cumplir-tarea). */
export const CumplirTareaEstadoSchema = z.enum(['cumplida', 'error']);

export const CumplirTareaInfoSchema = z.object({
  estado: CumplirTareaEstadoSchema.nullable(),
  fecha: z.string().nullable(),
});

/** Un paso de la anulación (egreso o remito): cuándo y quién lo hizo. */
export const AnulacionInfoSchema = z.object({
  at: z.string(),
  por: z.string().nullable(),
});

/**
 * Unidad pickeada de un despacho (stock en `p=1`, o `o=1` si ya egresó). La
 * guarda el back en cada lectura válida del pickeo; con esto la pantalla
 * reconstruye el pickeo al abrir el comprobante.
 */
export const PickeoRegistradoSchema = z.object({
  stockId: z.number(),
  /** Partida del remito a la que quedó atada (erp_task_detail.id). */
  erpTaskDetailId: z.number().nullable(),
  gtin: z.string().nullable(),
  serie: z.string(),
  lote: z.string().nullable(),
  /** `YYYY-MM-DD` (de la partida del ERP). */
  vencimiento: z.string().nullable(),
  sku: z.string().nullable(),
  estado: z.enum(['pickeada', 'egresada']),
  pickeadoAt: z.string().nullable(),
  usuario: z.string().nullable(),
});

/**
 * Si el pickeo habilita la traza. Lo calcula el back (misma regla con la que
 * rechaza la traza): en remitos por lote, pickeo obligatorio y completo.
 * `requerido` false = remito por serie, el pickeo no condiciona.
 */
export const PickeoTrazaSchema = z.object({
  requerido: z.boolean(),
  completo: z.boolean(),
  esperadas: z.number(),
  pickeadas: z.number(),
  motivo: z.string().nullable(),
});

export const ComprobanteDetalleSchema = z.object({
  tipo: TareaTipoSchema.exclude(['otro']),
  referencia: z.string(),
  /** ID de la tarea en ACSIS (erp_task.erp_id) — el que se muestra como
   *  "ID (ACSIS)" en el detalle y el que matchea con el log persistido. */
  erpId: z.string().nullable().optional(),
  /** ID local en nuestra DB (erp_task.id) — distinto al erpId de ACSIS. */
  localId: z.number().nullable().optional(),
  /** Número formateado AFIP, ej. "R-0001-00043718". Null si faltan datos. */
  numeroAfip: z.string().nullable(),
  /** GLN destino guardado (erp_task.gln_destino ← barrio del domicilio). */
  glnDestino: z.string().nullable().optional(),
  comprobante: ComprobanteHeaderSchema,
  items: z.array(ComprobanteItemSchema),
  partidas: z.array(ComprobantePartidaSchema),
  /** Series pickeadas del despacho, en orden de pickeo. */
  pickeos: z.array(PickeoRegistradoSchema).default([]),
  pickeoTraza: PickeoTrazaSchema.optional(),
  /** Resultado del cierre de tarea en el ERP. Optional por compat. */
  cumplirTarea: CumplirTareaInfoSchema.optional(),
  /** Anulación de la traza (2 pasos). null si ese paso no se hizo. */
  egresoAnulado: AnulacionInfoSchema.nullable().optional(),
  remitoAnulado: AnulacionInfoSchema.nullable().optional(),
});

export type ComprobanteItem = z.infer<typeof ComprobanteItemSchema>;
export type ComprobantePartida = z.infer<typeof ComprobantePartidaSchema>;
export type ComprobanteHeader = z.infer<typeof ComprobanteHeaderSchema>;
export type ComprobanteDetalle = z.infer<typeof ComprobanteDetalleSchema>;
export type PickeoRegistrado = z.infer<typeof PickeoRegistradoSchema>;
export type CumplirTareaEstado = z.infer<typeof CumplirTareaEstadoSchema>;
export type CumplirTareaInfo = z.infer<typeof CumplirTareaInfoSchema>;
